import { SafetyKeyPointsData, SafetyKeyPoint } from '../types';
import safetyKeyPointsData from '../data/safetyKeyPoints.json';

// Load the centralized safety key points data
export const safetyKeyPoints: SafetyKeyPointsData = safetyKeyPointsData;

// Helper function to get all variations for a specific category
export const getKeyPointsByCategory = (category: string): SafetyKeyPoint[] => {
  return safetyKeyPoints.keypoints.filter(kp =>
    kp.key_point.startsWith(category + ':')
  );
};

// Helper function to get all variations as a flat array for a category
export const getVariationsByCategory = (category: string): string[] => {
  const keyPoints = getKeyPointsByCategory(category);
  return keyPoints.flatMap(kp => kp.variations);
};

// Helper function to get all variations for multiple categories
export const getVariationsForCategories = (categories: string[]): string[] => {
  return categories.flatMap(category => getVariationsByCategory(category));
};

// Get all safety notes for a section by combining relevant categories
export const getSafetyNotesForSection = (sectionId: string): string[] => {
  const baseSafetyNote = "Proper eye protection must be worn—operate only with instructor's permission and after proper instructions have been received";

  switch (sectionId) {
    case 'general-safety':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Equipment Inspection',
          'Workspace Environment',
          'Operation Techniques',
          'Emergency Procedures',
          'Post-Operation Safety',
          'Communication Safety'
        ])
      ];

    case 'hand-tools':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Equipment Inspection',
          'Operation Techniques',
          'Workspace Environment'
        ])
      ];

    case 'portable-power-tools':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Equipment Inspection',
          'Power & Electrical',
          'Workpiece Preparation',
          'Operation Techniques'
        ])
      ];

    case 'power-equipment':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Equipment Inspection',
          'Power & Electrical',
          'Operation Techniques',
          'Post-Operation Safety',
          'Communication Safety'
        ])
      ];

    case 'portable-circular-saw':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Workpiece Preparation',
          'Workspace Environment',
          'Equipment Inspection',
          'Operation Techniques',
          'Post-Operation Safety'
        ])
      ];

    case 'jig-bayonet-saw':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Blade/Bit Condition',
          'Workpiece Preparation',
          'Operation Techniques',
          'Post-Operation Safety'
        ])
      ];

    case 'band-saw':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Workspace Environment',
          'Operation Techniques',
          'Post-Operation Safety',
          'Communication Safety'
        ])
      ];

    case 'portable-drill':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Blade/Bit Condition',
          'Workpiece Preparation',
          'Power & Electrical',
          'Operation Techniques',
          'Emergency Procedures'
        ])
      ];

    case 'drill-press':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Workpiece Preparation',
          'Blade/Bit Condition',
          'Equipment Inspection',
          'Operation Techniques',
          'Emergency Procedures'
        ])
      ];

    case 'soldering-station':
      return [
        baseSafetyNote,
        ...getVariationsForCategories([
          'PPE & Attire',
          'Instructor Authorization',
          'Equipment Inspection',
          'Post-Operation Safety'
        ])
      ];

    default:
      return [baseSafetyNote];
  }
};

// Get key points for free-response evaluation (legacy flat format)
export const getEvaluationKeyPoints = (): string[] => {
  return getVariationsForCategories([
    'PPE & Attire',
    'Instructor Authorization',
    'Equipment Inspection',
    'Workpiece Preparation',
    'Setup Procedures'
  ]);
};

// Get structured key points for proper evaluation
export const getStructuredEvaluationKeyPoints = () => {
  const categories = [
    'PPE & Attire',
    'Instructor Authorization',
    'Equipment Inspection',
    'Workpiece Preparation',
    'Setup Procedures'
  ];

  return categories.map(category => {
    const keyPoints = getKeyPointsByCategory(category);
    return keyPoints.map(kp => ({
      category,
      keyPoint: kp.key_point,
      description: kp.description,
      variations: kp.variations
    }));
  }).flat();
};